import { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  CubeIcon,
  ArrowPathIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api, { getErrorMessage } from '@/services/api';
import { SearchableSelect } from '@/components/common/SearchableSelect';

interface APIVersion {
  id: string;
  version: string;
  endpoint_base: string;
  documentation_url: string;
  is_active: boolean;
}

interface APIProduct {
  id: string;
  name: string;
  code: string;
  description: string;
  current_version: string;
  is_active: boolean;
  versions: APIVersion[];
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: APIProduct[];
}

interface ProductFormData {
  name: string;
  code: string;
  description: string;
}

interface VersionFormData {
  version: string;
  endpoint_base: string;
  documentation_url: string;
}

interface BrokerEnableFormData {
  broker_id: string;
  version_id: string;
}

const initialProductForm: ProductFormData = {
  name: '',
  code: '',
  description: '',
};

const initialVersionForm: VersionFormData = {
  version: '',
  endpoint_base: '',
  documentation_url: '',
};

const initialBrokerEnableForm: BrokerEnableFormData = {
  broker_id: '',
  version_id: '',
};

export function APIProductsPage() {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<APIProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [showEnableBrokerModal, setShowEnableBrokerModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<APIProduct | null>(null);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [productForm, setProductForm] = useState<ProductFormData>(initialProductForm);
  const [versionForm, setVersionForm] = useState<VersionFormData>(initialVersionForm);
  const [brokerEnableForm, setBrokerEnableForm] = useState<BrokerEnableFormData>(initialBrokerEnableForm);
  const [submitting, setSubmitting] = useState(false);

  const pageSize = 20;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<PaginatedResponse>(`/insurers/api-products/?page=${page}&page_size=${pageSize}`);
      setProducts(response.data.results);
      setTotalCount(response.data.count);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchProducts();
  }, [page, fetchProducts]);

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productForm.name || !productForm.code) {
      toast.error('Name and code are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/insurers/api-products/', productForm);
      toast.success('API product created successfully');
      setShowAddProductModal(false);
      setProductForm(initialProductForm);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!versionForm.version || !selectedProduct) {
      toast.error('Version is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/insurers/api-products/${selectedProduct.id}/add_version/`, versionForm);
      toast.success('Version added successfully');
      setShowAddVersionModal(false);
      setVersionForm(initialVersionForm);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnableBroker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brokerEnableForm.broker_id || !brokerEnableForm.version_id || !selectedProduct) {
      toast.error('Broker ID and Version are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/insurers/api-products/${selectedProduct.id}/enable_for_broker/`, brokerEnableForm);
      toast.success('Product enabled for broker successfully');
      setShowEnableBrokerModal(false);
      setBrokerEnableForm(initialBrokerEnableForm);
      setSelectedProduct(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const openAddVersionModal = (product: APIProduct) => {
    setSelectedProduct(product);
    setVersionForm(initialVersionForm);
    setShowAddVersionModal(true);
  };

  const openEnableBrokerModal = (product: APIProduct) => {
    setSelectedProduct(product);
    setBrokerEnableForm(initialBrokerEnableForm);
    setShowEnableBrokerModal(true);
  };

  const toggleExpand = (productId: string) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Products</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage API products and their versions
          </p>
        </div>
        <button
          onClick={() => {
            setProductForm(initialProductForm);
            setShowAddProductModal(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {/* Products Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <CubeIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">No API products found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by creating your first API product.</p>
            <button
              onClick={() => {
                setProductForm(initialProductForm);
                setShowAddProductModal(true);
              }}
              className="mt-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Product
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Current Version
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {products.map((product) => (
                    <>
                      <tr
                        key={product.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                              <CubeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{product.name}</p>
                              {product.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                  {product.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                            {product.code}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                          {product.current_version || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 text-sm font-medium rounded-full ${
                              product.is_active
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleExpand(product.id)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                              title="View versions"
                            >
                              <ArrowPathIcon className="w-3.5 h-3.5" />
                              Versions ({product.versions.length})
                            </button>
                            <button
                              onClick={() => openAddVersionModal(product)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 px-2.5 py-1.5 rounded-lg transition-colors"
                              title="Add version"
                            >
                              <PlusIcon className="w-3.5 h-3.5" />
                              Version
                            </button>
                            <button
                              onClick={() => openEnableBrokerModal(product)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg transition-colors"
                              title="Enable for broker"
                            >
                              <LinkIcon className="w-3.5 h-3.5" />
                              Enable Broker
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Versions Row */}
                      {expandedProduct === product.id && product.versions.length > 0 && (
                        <tr key={`${product.id}-versions`}>
                          <td colSpan={5} className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50">
                            <div className="ml-13">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                Versions
                              </h4>
                              <div className="space-y-2">
                                {product.versions.map((version) => (
                                  <div
                                    key={version.id}
                                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                  >
                                    <div className="flex items-center gap-4">
                                      <span className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
                                        v{version.version}
                                      </span>
                                      {version.endpoint_base && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {version.endpoint_base}
                                        </span>
                                      )}
                                      {version.documentation_url && (
                                        <a
                                          href={version.documentation_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-primary-600 dark:text-primary-400 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                                        >
                                          Docs
                                        </a>
                                      )}
                                    </div>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                                        version.is_active
                                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                      }`}
                                    >
                                      {version.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {expandedProduct === product.id && product.versions.length === 0 && (
                        <tr key={`${product.id}-no-versions`}>
                          <td colSpan={5} className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50">
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                              No versions available. Add the first version for this product.
                            </p>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages} ({totalCount} results)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddProductModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add API Product</h3>
                <button
                  onClick={() => setShowAddProductModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAddProduct}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        placeholder="Enter product name"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={productForm.code}
                        onChange={(e) => setProductForm({ ...productForm, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., MOTOR_API"
                        required
                        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      placeholder="Describe the API product..."
                      rows={3}
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    />
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddProductModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    )}
                    Add Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Version Modal */}
      {showAddVersionModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddVersionModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Version</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    for {selectedProduct.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowAddVersionModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAddVersion}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Version <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={versionForm.version}
                      onChange={(e) => setVersionForm({ ...versionForm, version: e.target.value })}
                      placeholder="e.g., 1.0.0"
                      required
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Endpoint Base
                    </label>
                    <input
                      type="text"
                      value={versionForm.endpoint_base}
                      onChange={(e) => setVersionForm({ ...versionForm, endpoint_base: e.target.value })}
                      placeholder="e.g., https://api.example.com/v1"
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Documentation URL
                    </label>
                    <input
                      type="url"
                      value={versionForm.documentation_url}
                      onChange={(e) => setVersionForm({ ...versionForm, documentation_url: e.target.value })}
                      placeholder="e.g., https://docs.example.com/v1"
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddVersionModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    )}
                    Add Version
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enable for Broker Modal */}
      {showEnableBrokerModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEnableBrokerModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Enable for Broker</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {selectedProduct.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowEnableBrokerModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleEnableBroker}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Broker ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={brokerEnableForm.broker_id}
                      onChange={(e) => setBrokerEnableForm({ ...brokerEnableForm, broker_id: e.target.value })}
                      placeholder="Enter broker UUID"
                      required
                      className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Version <span className="text-red-500">*</span>
                    </label>
                    {selectedProduct.versions.length > 0 ? (
                      <SearchableSelect
                        required
                        value={brokerEnableForm.version_id}
                        onChange={(value) => setBrokerEnableForm({ ...brokerEnableForm, version_id: value })}
                        placeholder="Select a version"
                        options={selectedProduct.versions.map((v) => ({
                          value: v.id,
                          label: `v${v.version} ${v.is_active ? '(Active)' : '(Inactive)'}`,
                        }))}
                      />
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={brokerEnableForm.version_id}
                          onChange={(e) => setBrokerEnableForm({ ...brokerEnableForm, version_id: e.target.value })}
                          placeholder="Enter version UUID"
                          required
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          No versions found. Enter a version ID manually.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEnableBrokerModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {submitting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    )}
                    Enable for Broker
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
